import React, {useEffect, useRef, useState} from 'react';

import {
    getAfkConfigByCompanyId,
    getAutoConnectStatus,
    getClientExpirationTime,
    getClientWebChatConf,
    getCompanyConvVis,
    getCompanyMessageConfig,
    getCompanyReminderConfigs,
    getCompanyTextAction,
    getCompanyWorkingHours,
    getOperatorInactivityActionConfigByCompanyId
} from "../../../../../../../services/company/api";

import config from "../../../../../../../config";
import AutoConnectConfig from "./components/AutoConnectConfig";
import ReminderSettings from "./components/ReminderSettings";
import ExpirationSettings from "./components/ExpirationSettings";
import TriggerSettings from "./components/TriggerSettings";
import TextActionSelector from "./components/TextActionSelector";
import WorkingHoursSettings from "./components/WorkingHoursSettings";
import MessageConfig from "./components/MessageConfig";
import ContactInfoRatingSettings from "./components/ContactInfoRatingSettings";
import ConversationVisibilityConfig from "./components/ConversationVisibilityConfig";
import ChatInactivityAction from "./components/ChatInactivityAction";
import OperatorInactivityAction from "./components/OperatorInactivityAction";
import LoadingComponent from "../../../../../../../components-new/ui/LoadingComponent";
import Tabs from "../../../../../../../components-new/ui/Tabs";
import PeopleIcon from "../../../../../../../assets/icons/PeopleIcon";
import BotIcon from "../../../../../../../assets/icons/BotIcon";
import ClockArrowUpIcon from "../../../../../../../assets/icons/ClockArrowUpIcon";
import Translate from "../../../../../../../components/Translate";
import useAvailableHeight from "../../../../../../../utils/hooks/useAvailableHeight";
import "./Functions.css";

const displayBotTab = (features, client) => {
    return (
        features.includes(config.features.EXPIRATION_TIME) ||
        (features.includes(config.features.TEXT_ACTION_NO_AI) && client.type === config.companyTypeMap.scenario) ||
        features.includes(config.features.REMINDERS_NO_AI)
    )
}

const Functions = ({tariff, client, user, addNotification}) => {
    const {features} = tariff;
    const [expiration, setExpiration] = useState();
    const [afkConfig, setAfkConfig] = useState({});
    const [operatorInactivityAction, setOperatorInactivityAction] = useState({enabled: false});
    const [triggers, setTriggers] = useState({openAfterPeriod: -1});
    const [textAction, setTextAction] = useState("");
    const [convVis, setConvVis] = useState("");
    const [workingHours, setWorkingHours] = useState({
        timezone: null,
        moveToUnmanagedList: false,
        enabled: false,
        nonWorkingDays: [],
        weekDayModeEnabled: false,
        weekdayHours: {}
    });
    const [reminderData, setReminderData] = useState({});
    const [messageConfigData, setMessageConfigData] = useState({});
    const [autoConnectInfo, setAutoConnectInfo] = useState({
        enabled: false,
        type: 'DISTRIBUTE_EQUALLY',
        notFoundType: 'MOVE_TO_UN_MANAGED_LIST',
    });

    const [isFetching, setIsFetching] = useState(false);

    const tabs = [
        // Voice-message permission is a company setting rather than a tariff
        // feature, so every bot has at least one operator setting to show.
        {
            label: 'operators',
            value: 'operators',
            icon: PeopleIcon,
            disabled: isFetching
        },
        ...(displayBotTab(features, client) ? [{label: 'bot', value: 'bot', icon: BotIcon, disabled: isFetching}] : []),
        ...(features.includes(config.features.WORKING_HOURS) ? [{
            label: 'operatorWorkingHours',
            value: 'workingHours',
            icon: ClockArrowUpIcon,
            disabled: isFetching
        }] : []),
        {
            label: 'other',
            value: 'other',
            icon: null,
            disabled: isFetching
        },
    ];

    const [activeTab, setActiveTab] = useState(tabs[0].value);

    const containerRef = useRef(null);
    // A fixed height rather than a cap, so the section keeps the same band on
    // every tab and is the only thing that scrolls. Remeasured on a tab change,
    // since each tab carries a different number of cards.
    const availableHeight = useAvailableHeight(containerRef, [activeTab]);

    useEffect(() => {
        setIsFetching(true);

        const promises = [
            getClientExpirationTime(client.id).then(setExpiration),
            getAfkConfigByCompanyId(client.id).then(setAfkConfig),
            getOperatorInactivityActionConfigByCompanyId(client.id).then(setOperatorInactivityAction),
            getClientWebChatConf(client.id).then((data) => setTriggers(data.triggers)),
            getCompanyTextAction(client.id).then(setTextAction),
            getCompanyConvVis(client.id).then(setConvVis),
            getCompanyWorkingHours(client.id).then((data) => {
                setWorkingHours({
                    timezone: data ? data.timezone || 'UTC' : null,
                    moveToUnmanagedList: (data && data.moveToUnmanagedList) || false,
                    enabled: (data && data.enabled) || false,
                    nonWorkingDays: (data && data.nonWorkingDays) || [],
                    weekdayHours: (data && data.weekdayHours) || {},
                });
            }),
            getCompanyReminderConfigs(client.id).then(setReminderData),
            getCompanyMessageConfig(client.id).then(setMessageConfigData)
        ];

        if (features.includes(config.features.OPERATOR_CONNECT_LOGIC)) {
            promises.push(
                getAutoConnectStatus(client.id)
                    .then(setAutoConnectInfo)
                    .catch(err => console.error(err))
            );
        }

        Promise.allSettled(promises).finally(() => {
            setIsFetching(false)
        });

    }, [client.id]);

    return (
        <div
            className="settings-tab"
            ref={containerRef}
            style={availableHeight ? {height: availableHeight + 'px'} : undefined}
        >
            {isFetching && (
                <div className="settings-tab__loading">
                    <LoadingComponent/>
                </div>
            )}

            <Tabs
                tabs={tabs}
                selectedTab={activeTab}
                onTabChange={t => setActiveTab(t)}
                renderTab={t => {
                    const Icon = t.icon;
                    return (
                        <div style={{display: 'flex', gap: '6px', alignItems: 'center'}}>
                            {t.icon && <Icon width={16} height={16} style={{flexShrink: 0}}/>}
                            <Translate text={t.label}/>
                        </div>
                    );
                }}
            />

            <div className="settings-tab__body">
                {activeTab === 'operators' && (
                    <React.Fragment>
                        {features.includes(config.features.OPERATOR_CONNECT_LOGIC) && (
                            <AutoConnectConfig
                                features={features}
                                client={client}
                                user={user}
                                autoConnectInfo={autoConnectInfo}
                                setAutoConnectInfo={setAutoConnectInfo}
                                isFetching={isFetching}
                            />
                        )}

                        {features.includes(config.features.CONVERSATION_VISIBILITY) && (
                            <ConversationVisibilityConfig
                                features={features}
                                client={client}
                                user={user}
                                convVis={convVis}
                                setConvVis={setConvVis}
                                disabled={autoConnectInfo.enabled}
                                isFetching={isFetching}
                            />
                        )}

                        {features.includes(config.features.OPERATOR_INACTIVITY_ACTION) && (
                            <OperatorInactivityAction
                                user={user}
                                setOperatorInactivityAction={setOperatorInactivityAction}
                                operatorInactivityAction={operatorInactivityAction}
                                client={client}
                                isFetching={isFetching}
                            />
                        )}

                        <MessageConfig
                            features={features}
                            client={client}
                            messageConfigData={messageConfigData}
                            setMessageConfigData={setMessageConfigData}
                            isFetching={isFetching}
                        />
                    </React.Fragment>
                )}

                {activeTab === 'bot' && (
                    <React.Fragment>
                        {features.includes(config.features.EXPIRATION_TIME) && (
                            <ExpirationSettings expiration={expiration} client={client} isFetching={isFetching}/>
                        )}

                        {features.includes(config.features.TEXT_ACTION_NO_AI)
                            && client.type === config.companyTypeMap.scenario && (
                                <TextActionSelector
                                    features={features}
                                    client={client}
                                    user={user}
                                    textAction={textAction}
                                    setTextAction={setTextAction}
                                    isFetching={isFetching}
                                />
                            )}

                        {features.includes(config.features.REMINDERS_NO_AI) && (
                            <ReminderSettings
                                features={features}
                                client={client}
                                user={user}
                                reminderData={reminderData}
                                setReminderData={setReminderData}
                                isFetching={isFetching}
                            />
                        )}
                    </React.Fragment>
                )}

                {activeTab === 'workingHours' && features.includes(config.features.WORKING_HOURS) && (
                    <WorkingHoursSettings
                        addNotification={addNotification}
                        client={client}
                        workingHours={workingHours}
                        setWorkingHours={setWorkingHours}
                        isFetching={isFetching}
                    />
                )}

                {activeTab === 'other' && (
                    <React.Fragment>
                        <ContactInfoRatingSettings key={client.id} client={client} addNotification={addNotification}/>
                        {features.includes(config.features.USER_INACTIVITY_ACTION) && (
                            <ChatInactivityAction
                                setAfkConfig={setAfkConfig}
                                afkConfig={afkConfig}
                                client={client}
                                isFetching={isFetching}
                            />
                        )}

                        {features.includes(config.features.WEBCHAT_TRIGGER) && (
                            <TriggerSettings
                                triggers={triggers}
                                setTriggers={setTriggers}
                                clientId={client.id}
                                isFetching={isFetching}
                            />
                        )}
                    </React.Fragment>
                )}
            </div>
        </div>
    );
};

export default Functions;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/clients/new/functions/Functions.jsx